// src/utils/dialog.js
// Styled, promise-based replacements for the browser's window.confirm / alert.
// Usage:
//   if (!(await confirmDialog({ message: 'Delete this?', tone: 'danger' }))) return;
//   await alertDialog({ message: 'Saved!' });
// Each call mounts a small React tree into a throwaway DOM node and resolves
// when the user clicks a button (or presses Enter/Escape / clicks the backdrop).
import React from 'react';
import { createRoot } from 'react-dom/client';
import { AlertTriangleIcon, TrashIcon, CheckCircleIcon } from '../components/Icon';

function mount(renderWithCleanup) {
  const host = document.createElement('div');
  document.body.appendChild(host);
  const root = createRoot(host);
  const cleanup = () => { root.unmount(); host.remove(); };
  root.render(renderWithCleanup(cleanup));
}

function DialogIcon({ tone }) {
  if (tone === 'danger')  return <TrashIcon size={34} />;
  if (tone === 'success') return <CheckCircleIcon size={36} />;
  return <AlertTriangleIcon size={36} />;
}

function AppDialog({ title, message, confirmText, cancelText, tone, alertOnly, onConfirm, onCancel }) {
  React.useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onCancel();
      else if (e.key === 'Enter') onConfirm();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onConfirm, onCancel]);

  return (
    <div className="appdlg-overlay" onClick={e => e.target === e.currentTarget && onCancel()}>
      <div className="appdlg" role="dialog" aria-modal="true">
        <div className={`appdlg-icon appdlg-icon--${tone}`}><DialogIcon tone={tone} /></div>
        {title && <div className="appdlg-title">{title}</div>}
        <div className="appdlg-text">{message}</div>
        <div className="appdlg-actions">
          {!alertOnly && (
            <button className="appdlg-cancel" onClick={onCancel}>{cancelText}</button>
          )}
          <button className={`appdlg-ok appdlg-ok--${tone}`} onClick={onConfirm} autoFocus>{confirmText}</button>
        </div>
      </div>
    </div>
  );
}

// Confirmation dialog → resolves true (confirmed) or false (cancelled).
export function confirmDialog({
  title = 'Please Confirm',
  message = '',
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  tone = 'gold',          // 'gold' | 'danger' | 'success'
} = {}) {
  return new Promise(resolve => {
    mount(cleanup => (
      <AppDialog
        title={title} message={message}
        confirmText={confirmText} cancelText={cancelText} tone={tone}
        onConfirm={() => { cleanup(); resolve(true); }}
        onCancel={() => { cleanup(); resolve(false); }}
      />
    ));
  });
}

// Notice dialog (single OK button) → resolves when dismissed.
export function alertDialog({
  title = 'Notice',
  message = '',
  confirmText = 'OK',
  tone = 'gold',
} = {}) {
  return new Promise(resolve => {
    mount(cleanup => (
      <AppDialog
        title={title} message={message} confirmText={confirmText} tone={tone} alertOnly
        onConfirm={() => { cleanup(); resolve(true); }}
        onCancel={() => { cleanup(); resolve(true); }}
      />
    ));
  });
}
